// The category hubs' phone map: the Stays phone map (owner, 2026-10-04: "for
// map we need the same map ux of stays"), with the hub's own controls in the
// sheet: the city, Filters and, on Restaurants, the collections. No row of
// other categories: the map is this hub's places only (owner, 2026-10-04).
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { hubCollections } from "@majourneys/shared";
import { useCollectionLabels } from "../lib/hub-collections";
import { useCitiesOptional } from "../hooks/use-cities";
import type { ActivityFilters } from "../constants/query-keys";
import { openHubFilters, useCityCounts, useHubFiltersCount, type CityRow } from "./hub-dialogs";
import { setHubLoading } from "./hub-top";
import { PhoneMap, type PhoneMapApi, type PhoneMapStay } from "../../stays/components/stays-phone-map";
import type { StaysMapPin } from "../../stays/components/stays-hub-map-lazy";
import { MoodGlyph } from "../../stays/components/mood-glyph";
import { CONTROL_EDGE, CONTROL_LIT } from "../../../shared/ui/pewter-edge";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const META = "#5a5d77";
const CONTROL = { background: CONTROL_LIT, color: NAVY, boxShadow: `inset 0 0 0 1px ${CONTROL_EDGE}` } as const;
/** A chip or button at rest: the soft-bevel white face over the same hairline. The city pill keeps CONTROL: it opens a picker. */
const CONTROL_BEVEL = { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, inset 0 0 0 1px ${CONTROL_EDGE}` } as const;
const CHIP_ON = { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON } as const;
// A sideways scroller clips vertically too: room above and below for the
// chips' ring, handed back by the negative top margin.
const ROW = "-mx-4 -mt-1.5 flex gap-2 overflow-x-auto px-4 pb-3 pt-2.5 [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden";
const CHIP = "inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full pe-1.5 ps-3.5 text-[13px] font-semibold no-underline transition-[transform,background,color] duration-150 active:scale-[0.97]";

export interface HubPhoneMapProps {
  lang: string;
  onClose: () => void;
  pins: StaysMapPin[];
  items: Map<string, PhoneMapStay>;
  fitKey: string;
  total: number;
  categorySlug?: string;
  citySlug?: string;
  /** The page's filters, for the per-city counts. */
  filters: ActivityFilters;
  /** Restaurants only: the collection chips. */
  collections?: { counts: Record<string, number> | undefined; active: string | undefined; onChange: (tag: string | undefined) => void };
}

export function HubPhoneMap(props: HubPhoneMapProps) {
  const { t } = useTranslation();
  // Picking a city: the sheet's list becomes the city list.
  const [pickingCity, setPickingCity] = useState(false);
  return (
    <PhoneMap
      label={t("explore.phone_map.label")}
      onClose={props.onClose}
      pins={props.pins}
      items={props.items}
      fitKey={props.fitKey}
      cta={t("explore.phone_map.see_details")}
      swipeHint={t("explore.phone_map.swipe_next")}
      pinVariant="photo"
      body={pickingCity ? <CityList {...props} onPicked={() => setPickingCity(false)} /> : undefined}
      controls={(api) => (
        <HubSheetControls
          {...props}
          api={api}
          pickingCity={pickingCity}
          onCityPill={() => {
            const next = !pickingCity;
            setPickingCity(next);
            api.setSnap(next ? "full" : "mid");
          }}
        />
      )}
    />
  );
}

function HubSheetControls({
  lang,
  categorySlug,
  citySlug,
  total,
  collections,
  api,
  pickingCity,
  onCityPill,
}: HubPhoneMapProps & { api: PhoneMapApi; pickingCity: boolean; onCityPill: () => void }) {
  const { t } = useTranslation();
  const filterCount = useHubFiltersCount();
  const { data: cities } = useCitiesOptional();
  const cityName = ((cities?.data ?? []) as CityRow[]).find((c) => c.slug === citySlug)?.name;
  const collectionLabels = useCollectionLabels(categorySlug);
  const shownCollections = collections
    ? hubCollections(categorySlug).filter((c) => c.id === collections.active || (collections.counts?.[c.id] ?? 0) > 0)
    : [];
  return (
    <>
      {/* The page's collection chips, with their counts, on top, where the
          Stays map has its types (owner, 2026-10-04). */}
      {collections && shownCollections.length ? (
        <div className={ROW} role="group" aria-label={collectionLabels.groupLabel}>
          {shownCollections.map((c) => {
            const on = c.id === collections.active;
            const count = collections.counts?.[c.id];
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                onClick={() => collections.onChange(on ? undefined : c.id)}
                className={CHIP}
                style={on ? CHIP_ON : CONTROL_BEVEL}
              >
                <MoodGlyph icon={c.icon} on={on} size={14} />
                {collectionLabels.label(c.id)}
                {count !== undefined ? (
                  // The count in its own soft badge, as on the page's chips, so
                  // it never runs into the label or the pill's edge.
                  <span
                    className="ms-0.5 rounded-full px-2 py-0.5 text-[12px] font-semibold tabular-nums"
                    style={on ? { background: "rgba(255,255,255,0.16)" } : { background: "rgba(17,14,57,0.055)", color: META }}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCityPill}
          aria-expanded={pickingCity}
          className="flex min-h-12 py-1.5 min-w-0 flex-1 items-center gap-2 rounded-full px-4 text-start text-[14px]"
          style={{ ...CONTROL, color: META }}
        >
          <Icon name="ri-map-pin-2-line" className="shrink-0 text-[17px]" aria-hidden="true" />
          <span className="min-w-0 flex-1 break-words leading-tight">{cityName ?? t("explore.all_of_morocco")}</span>
          <Icon
            name="ri-arrow-down-s-line"
            className={`shrink-0 text-[18px] transition-transform duration-200 ${pickingCity ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          aria-label={t("filter.filters", { defaultValue: "Filters" })}
          onClick={openHubFilters}
          className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
          style={CONTROL_BEVEL}
        >
          <Icon name="ri-equalizer-line" className="text-[18px]" aria-hidden="true" />
          {filterCount ? (
            <span className="absolute -end-0.5 -top-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold text-white" style={{ background: NAVY }}>
              {filterCount}
            </span>
          ) : null}
        </button>
      </div>
      <div className="h-3" aria-hidden />
      {pickingCity ? null : (
        <p className="m-0 flex items-center justify-center gap-1.5 text-[12.5px] font-semibold" style={{ color: META }}>
          {api.narrowed ? t("explore.phone_map.count_area", { count: api.inView }) : t("explore.place_count", { count: total })}
          {api.narrowed ? (
            <>
              <span aria-hidden="true">·</span>
              <button type="button" onClick={api.showAll} className="font-bold underline underline-offset-2" style={{ color: NAVY }}>
                {t("stays.hub_map.show_all")}
              </button>
            </>
          ) : null}
        </p>
      )}
    </>
  );
}

/** Every city with places in this hub, busiest first, as links. */
function CityList({ lang, categorySlug, citySlug, filters, onPicked }: HubPhoneMapProps & { onPicked: () => void }) {
  const { t } = useTranslation();
  const { data } = useCitiesOptional();
  const { data: counts } = useCityCounts(filters, !!categorySlug);
  const rows = useMemo(() => {
    const all = (data?.data ?? []) as CityRow[];
    return all
      .map((c) => ({ ...c, n: counts?.complete ? counts.counts.get(c.slug) ?? 0 : c.itemsCount ?? 0 }))
      .filter((c) => (counts?.complete ? c.n > 0 || c.slug === citySlug : true))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [data, counts, citySlug]);
  if (!categorySlug) return null;
  const pick = (slug: string | undefined) => {
    if (slug !== citySlug) setHubLoading(true);
    onPicked();
  };
  const row = (on: boolean, name: string, n?: number, image?: string) => (
    <span
      className="flex min-h-14 items-center gap-3 rounded-2xl px-2 py-1.5"
      style={on ? { background: "#EEF0F6" } : undefined}
    >
      <span className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ background: "#EEF0F6", color: NAVY }}>
        {image ? (
          <img src={image.replace(/\/public$/, "/w=96,fit=scale-down,format=auto")} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <Icon name="ri-map-pin-2-line" className="text-[18px]" aria-hidden="true" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="break-words text-[15px] font-semibold" style={{ color: NAVY }}>{name}</span>
        {n !== undefined ? (
          <span className="text-[13px]" style={{ color: META }}>{t("explore.place_count", { count: n })}</span>
        ) : null}
      </span>
      {on ? <Icon name="ri-check-line" className="shrink-0 text-[18px]" style={{ color: NAVY }} aria-hidden="true" /> : null}
    </span>
  );
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      <li>
        <Link
          to="/$lang/category/$category"
          params={{ lang, category: categorySlug }}
          resetScroll={false}
          onClick={() => pick(undefined)}
          className="block no-underline"
        >
          {row(!citySlug, t("explore.all_of_morocco"), counts?.complete ? counts.total : undefined)}
        </Link>
      </li>
      {rows.map((c) => (
        <li key={c.slug}>
          <Link
            to="/$lang/category/$category/$city"
            params={{ lang, category: categorySlug, city: c.slug }}
            resetScroll={false}
            onClick={() => pick(c.slug)}
            className="block no-underline"
          >
            {row(c.slug === citySlug, c.name, counts?.complete ? c.n : undefined, c.image)}
          </Link>
        </li>
      ))}
    </ul>
  );
}
